
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Table, Alert, Card, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import trashIcon from '../asset/delete.svg';

// Module-level cache — survives navigation
const _employeeCache = {};

// Fallback avatar
const FALLBACK = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iNTAiIGZpbGw9IiNlMmU4ZjAiLz48dGV4dCB4PSI1MCIgeT0iNTUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5NGEzYjgiIGZvbnQtc2l6ZT0iMzAiPj88L3RleHQ+PC9zdmc+";

const Company = ({ selectedCompany: propSelectedCompany, navigate }) => {
  const [selectedCompany, setSelectedCompany]     = useState(propSelectedCompany || null);
  const [employees, setEmployees]                 = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [error, setError]                         = useState(null);
  const [loadingEmployees, setLoadingEmployees]   = useState(false);
  const [showDeleteModal, setShowDeleteModal]     = useState(false);
  const [deleteItemId, setDeleteItemId]           = useState(null);
  const [deleteItemName, setDeleteItemName]       = useState('');
  const [addressFilter, setAddressFilter]         = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const currentRequestId = useRef(0);

  const API_BASE_URL = `https://${window.location.hostname}`;
  const tabId        = sessionStorage.getItem("activeTab");
  const token        = tabId ? sessionStorage.getItem(`token_${tabId}`) : null;
  const authHeaders  = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  useEffect(() => {
    if (propSelectedCompany) {
      setSelectedCompany(propSelectedCompany);
      setAddressFilter(''); // reset filter on company switch
      fetchEmployees(propSelectedCompany);
    }
  }, [propSelectedCompany]);

  const fetchEmployees = async (companyName) => {
    // ── CACHE HIT: instant, no spinner ──
    if (_employeeCache[companyName]) {
      console.log("⏭️ Cache hit:", companyName);
      setEmployees(_employeeCache[companyName]);
      setFilteredEmployees(_employeeCache[companyName]);
      setLoadingEmployees(false);
      return;
    }

    const myRequestId = ++currentRequestId.current;

    setLoadingEmployees(true);
    setError(null);
    // Clear previous company's data immediately so stale data doesn't flash
    setEmployees([]);
    setFilteredEmployees([]);

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/employees/company/${encodeURIComponent(companyName)}`,
        { headers: authHeaders }
      );

      // ── Stale response — a newer request already started, discard this ──
      if (myRequestId !== currentRequestId.current) {
        console.log("⏭️ Discarding stale response for:", companyName);
        return;
      }

      if (res.status === 401 || res.status === 403) {
        setError("Session expired. Please login again.");
        return;
      }

      let data = await res.json();
      if (!Array.isArray(data)) data = [];

      const processed = data.map((emp) => ({
        ...emp,
        // imageUrl: emp.imageUrl || emp.faceJpgBase64
        //   ? (emp.imageUrl || `data:image/jpeg;base64,${emp.faceJpgBase64}`)
        //   : null,
      }));

      // Check again after await (async gap)
      if (myRequestId !== currentRequestId.current) return;

      _employeeCache[companyName] = processed;
      setEmployees(processed);
      setFilteredEmployees(processed);
    } catch (err) {
      if (myRequestId === currentRequestId.current) {
        setError(err.message);
      }
    } finally {
      if (myRequestId === currentRequestId.current) {
        setLoadingEmployees(false);
      }
    }
  };

  const handleDeleteEmployee = (id, name) => {
    setDeleteItemId(id);
    setDeleteItemName(name);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/employees/${deleteItemId}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Failed to delete employee");
      setShowDeleteModal(false);
      // Invalidate cache so next load is fresh
      delete _employeeCache[selectedCompany];
      fetchEmployees(selectedCompany);
    } catch (err) {
      setError(err.message);
      setShowDeleteModal(false);
    }
  };

  const handleViewLogs = (id) => navigate(`/person-logs/${id}`);

  // Address filter
  useEffect(() => {
    if (addressFilter.trim() === '') {
      setFilteredEmployees(employees);
    } else {
      const q = addressFilter.toLowerCase();
      setFilteredEmployees(
        employees.filter(emp => emp.address?.toLowerCase().includes(q))
      );
    }
  }, [addressFilter, employees]);
  useEffect(() => {
      let result = employees;
      if (nameFilter.trim() !== '') {
        const q = nameFilter.toLowerCase();
        result = result.filter(emp => emp.name?.toLowerCase().includes(q));
      }
      if (addressFilter.trim() !== '') {
        const q = addressFilter.toLowerCase();
        result = result.filter(emp => emp.address?.toLowerCase().includes(q));
      }
      setFilteredEmployees(result);
    }, [nameFilter, addressFilter, employees]);
    useEffect(() => {
      if (propSelectedCompany) {
        setSelectedCompany(propSelectedCompany);
        setAddressFilter('');
        setNameFilter('');   // ← add this
        fetchEmployees(propSelectedCompany);
      }
  }, [propSelectedCompany]);
  return (
    <div className="container-fluid p-4" style={{ background: "#f8fafc", minHeight: "100vh" }}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        <Col>
          {selectedCompany ? (
            <Card className="shadow-sm border-0">
              <Card.Header style={{ background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff" }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>
                    Employees for <strong>{selectedCompany}</strong>
                    {!loadingEmployees && (
                      <span className="ms-2 badge bg-white text-success fw-bold">
                        {filteredEmployees.length}
                      </span>
                    )}
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by name..."
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    style={{ maxWidth: 220, background: "rgba(255,255,255,0.9)", color: "#333" }}
                  />
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {loadingEmployees ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-success" role="status"></div>
                    <p className="mt-3 text-muted">Loading employees...</p>
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">
                      No employees found for <strong>{selectedCompany}</strong>.
                    </p>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Name</th>
                          <th>Company</th>
                          <th>Address</th>
                          <th style={{ width: 90 }}>Logs</th>
                          <th style={{ width: 70 }}>Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees.map((emp,index) => (
                          <EmployeeRow
                            key={emp.id}
                            emp={emp}
                            index={index}  
                            onViewLogs={handleViewLogs}
                            onDelete={handleDeleteEmployee}
                            // fallback={FALLBACK}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          ) : (
            <Card className="shadow-sm border-0 text-center">
              <Card.Body className="py-5">
                <h5 className="text-muted">Select a company from the sidebar</h5>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Delete Modal */}
      <Modal centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg,#dc3545,#b02a37)", color: "#fff" }}>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteItemName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const EmployeeRow = React.memo(({ emp, onViewLogs, onDelete, index }) => (
  <tr>
    <td className="py-2"style={{ fontSize: "0.85rem" }}>
      {index + 1}
    </td>
    <td className="py-3 fw-bold" style={{ fontSize: "0.95rem" }}>
      {emp.name}
    </td>
    <td className="py-3" style={{ fontSize: "0.82rem", color: "#6c7ae0" }}>
      <i className="fas fa-building me-1"></i>
      {emp.company_name}
    </td>
    <td className="py-3" style={{ maxWidth: 250, fontSize: "0.85rem", color: "#444" }}>
      <span title={emp.address}>
        {emp.address
          ? emp.address.length > 40 ? emp.address.substring(0, 40) + "..." : emp.address
          : "—"}
      </span>
    </td>
    <td className="py-3">
      <button
        className="btn btn-sm btn-outline-primary"
        style={{ borderRadius: "8px", padding: "4px 10px" }}
        onClick={() => onViewLogs(emp.id)}
      >
        <i className="fas fa-file-alt me-1"></i> Logs
      </button>
    </td>
    <td className="py-3">
      <button
        className="btn btn-sm btn-outline-danger"
        style={{ borderRadius: "8px", padding: "4px 10px" }}
        onClick={() => onDelete(emp.id, emp.name)}
      >
        <img src={trashIcon} alt="delete" style={{ width: 16, height: 16 }} />
      </button>
    </td>
  </tr>
));

export default Company;